// Notary Public (ทนายความผู้ทำคำรับรองลายมือชื่อและเอกสาร) × document-type landings.
// อ้างอิงกรอบวิชาชีพ: ข้อบังคับสภาทนายความว่าด้วยการขึ้นทะเบียนทนายความผู้ทำคำรับรอง
// ลายมือชื่อและเอกสาร พ.ศ. 2551 — ไทยไม่ได้เป็นภาคีอนุสัญญากรุงเฮก (Apostille) จนถึงปี 2026
// เอกสารที่ต้องใช้ต่างประเทศจึงมักต้องผ่านนิติกรณ์กรมการกงสุล + สถานทูตปลายทาง

export interface NotaryDocLanding {
  slug: string;
  titleTh: string;
  titleEn: string;
  /** ชื่อเอกสาร/แบบฟอร์มทางการ */
  officialTh: string;
  officialEn: string;
  /** ประเภทคำรับรองที่ทนาย Notarial Services Attorney ออกให้ */
  certTypeTh: string;
  certTypeEn: string;
  useCaseTh: string;
  useCaseEn: string;
  prepareTh: string[];
  prepareEn: string[];
  pitfallsTh: string[];
  pitfallsEn: string[];
  /** ขั้นตอนหลังรับรอง (นิติกรณ์ / สถานทูต) */
  nextStepTh: string;
  nextStepEn: string;
  turnaroundTh: string;
  turnaroundEn: string;
}

const D = (d: NotaryDocLanding): NotaryDocLanding => d;

export const NOTARY_DOC_LANDINGS: NotaryDocLanding[] = [
  D({
    slug: "power-of-attorney",
    titleTh: "รับรองหนังสือมอบอำนาจ (Power of Attorney)",
    titleEn: "Notarisation of Power of Attorney",
    officialTh: "หนังสือมอบอำนาจ / Power of Attorney (ทั่วไปหรือเฉพาะการ)",
    officialEn: "Power of Attorney (general or special/limited)",
    certTypeTh: "รับรองลายมือชื่อผู้มอบอำนาจต่อหน้าทนาย (Signature Witness / Acknowledgement)",
    certTypeEn: "Signature witnessing and acknowledgement before the notarial services attorney",
    useCaseTh:
      "ใช้มอบอำนาจให้ญาติหรือทนายในต่างประเทศดำเนินการแทน เช่น ขายบ้าน/ที่ดินในต่างประเทศ ปิดบัญชีธนาคาร จัดการมรดก ยื่นภาษี หรือจดทะเบียนบริษัท เอกสารต้องระบุขอบเขตอำนาจให้ชัด เพราะหลายประเทศไม่ยอมรับการมอบอำนาจแบบเปิดกว้าง",
    useCaseEn:
      "Used to appoint a relative or lawyer abroad to act on your behalf — selling property, closing bank accounts, estate administration, tax filings or company registration. The scope of authority must be specific; many jurisdictions reject open-ended authority.",
    prepareTh: [
      "หนังสือเดินทางหรือบัตรประชาชนตัวจริงของผู้มอบอำนาจ (ต้องมาลงลายมือชื่อต่อหน้าทนาย)",
      "ร่างหนังสือมอบอำนาจฉบับที่ปลายทางต้องการ — อย่าลงลายมือชื่อมาก่อน",
      "ข้อมูลผู้รับมอบอำนาจ: ชื่อตามหนังสือเดินทาง เลขเอกสาร ที่อยู่",
      "รายละเอียดทรัพย์สินหรือธุรกรรม เช่น เลขโฉนด เลขบัญชี เลขทะเบียนบริษัท",
    ],
    prepareEn: [
      "Original passport or Thai ID of the grantor — you must sign in front of the attorney",
      "The draft POA in the form required by the receiving party; do not pre-sign it",
      "Attorney-in-fact details: full name as per passport, ID number, address",
      "Transaction details such as title deed number, account number or company registration number",
    ],
    pitfallsTh: [
      "ลงลายมือชื่อมาจากบ้านก่อน — ทนายรับรองลายมือชื่อที่ไม่ได้ลงต่อหน้าไม่ได้ ต้องเซ็นใหม่",
      "ชื่อสะกดไม่ตรงหนังสือเดินทาง ทำให้ปลายทางปฏิเสธ",
      "ใช้แบบฟอร์มทั่วไปทั้งที่ปลายทาง (เช่น สหรัฐฯ บางรัฐ หรือญี่ปุ่น) มีแบบฟอร์มเฉพาะ",
      "ลืมขั้นตอนนิติกรณ์กรมการกงสุลและสถานทูตปลายทาง ทำให้เอกสารใช้ไม่ได้",
    ],
    pitfallsEn: [
      "Signing at home beforehand — the attorney can only certify a signature made in their presence",
      "Name spelling that differs from the passport, which the receiving party will reject",
      "Using a generic template when the destination (some US states, Japan) mandates its own form",
      "Skipping Department of Consular Affairs legalisation and destination embassy attestation",
    ],
    nextStepTh:
      "หลังทนายรับรอง หากปลายทางต้องการการรับรองระดับรัฐ ให้นำไปนิติกรณ์ที่กรมการกงสุล กระทรวงการต่างประเทศ แล้วจึงรับรองที่สถานทูต/สถานกงสุลของประเทศปลายทางในไทย",
    nextStepEn:
      "After notarisation, if the destination requires state-level authentication, legalise the document at the Department of Consular Affairs (MFA) and then at the destination country's embassy or consulate in Thailand.",
    turnaroundTh: "รับรองเสร็จภายในวันเดียว (นัดล่วงหน้า 1 วันทำการ) · นิติกรณ์ MFA เพิ่ม 1–3 วันทำการ",
    turnaroundEn: "Same-day notarisation with one working day's notice; MFA legalisation adds 1–3 working days.",
  }),
  D({
    slug: "affidavit",
    titleTh: "รับรองคำให้การเป็นหนังสือ (Affidavit / Statutory Declaration)",
    titleEn: "Notarisation of Affidavit and Statutory Declaration",
    officialTh: "Affidavit, Statutory Declaration, Declaration of Single Status (ฉบับที่ผู้ให้ถ้อยคำจัดทำเอง)",
    officialEn: "Affidavit, statutory declaration or declaration of single status prepared by the deponent",
    certTypeTh: "รับรองการสาบานตน/ปฏิญาณตน และลายมือชื่อผู้ให้ถ้อยคำ (Oath / Affirmation)",
    certTypeEn: "Administering the oath or affirmation and witnessing the deponent's signature",
    useCaseTh:
      "ใช้ยืนยันข้อเท็จจริงต่อหน่วยงานต่างประเทศ เช่น สถานะโสด ความสัมพันธ์ในครอบครัว รายได้ การสนับสนุนทางการเงิน หรือคำชี้แจงชื่อ-นามสกุลที่สะกดต่างกันในเอกสารหลายฉบับ",
    useCaseEn:
      "Confirms facts to overseas authorities — single status, family relationship, income, financial support, or explaining differing name spellings across documents.",
    prepareTh: [
      "ร่างคำให้การเป็นภาษาอังกฤษ ระบุข้อเท็จจริงเป็นข้อ ๆ ชัดเจน",
      "หนังสือเดินทาง/บัตรประชาชนตัวจริง",
      "เอกสารประกอบที่อ้างถึงในคำให้การ เช่น ทะเบียนบ้าน สูติบัตร statement ธนาคาร",
    ],
    prepareEn: [
      "A draft declaration in English with the facts set out in numbered paragraphs",
      "Original passport or Thai ID card",
      "Supporting documents referred to in the declaration (house registration, birth certificate, bank statements)",
    ],
    pitfallsTh: [
      "เขียนข้อความคลุมเครือหรือเป็นความเห็น ทนายรับรองได้เฉพาะการสาบานตน ไม่ได้รับรองว่าข้อเท็จจริงเป็นจริง",
      "ไม่แนบเอกสารประกอบ (exhibit) ที่กล่าวอ้าง",
      "สับสนกับ 'หนังสือรับรองความเป็นโสด' ที่ออกโดยสำนักงานเขต/อำเภอ ซึ่งเป็นคนละฉบับ",
    ],
    pitfallsEn: [
      "Vague or opinion-based wording — the attorney certifies the oath, not the truth of the facts",
      "Failing to attach the exhibits the declaration refers to",
      "Confusing it with the certificate of single status issued by a district office, which is a different document",
    ],
    nextStepTh:
      "หลายประเทศรับคำให้การที่ผ่านการรับรองของทนายโดยตรง แต่หากใช้กับทางราชการปลายทาง ควรผ่านนิติกรณ์กรมการกงสุลและสถานทูตปลายทางเพิ่ม",
    nextStepEn:
      "Many recipients accept the notarised declaration directly; for official use abroad, add MFA legalisation and destination embassy attestation.",
    turnaroundTh: "รับรองเสร็จภายในวันเดียว · ช่วยตรวจร่างก่อนนัดได้",
    turnaroundEn: "Same-day notarisation, with a free draft review before your appointment.",
  }),
  D({
    slug: "certified-true-copy",
    titleTh: "รับรองสำเนาถูกต้อง (Certified True Copy)",
    titleEn: "Certified True Copy of Documents",
    officialTh: "สำเนาหนังสือเดินทาง บัตรประชาชน ทะเบียนบ้าน ใบอนุญาต หรือหนังสือรับรองบริษัท",
    officialEn: "Copies of passports, ID cards, house registration, licences or company certificates",
    certTypeTh: "รับรองว่าสำเนาถูกต้องตรงกับต้นฉบับที่ได้ตรวจแล้ว (Certified True Copy)",
    certTypeEn: "Certification that the copy is a true copy of the original sighted by the attorney",
    useCaseTh:
      "ธนาคารต่างประเทศ โบรกเกอร์ แพลตฟอร์มคริปโต และผู้ให้บริการ KYC มักขอสำเนาหนังสือเดินทางที่ผ่านการรับรองจาก notary แทนการส่งต้นฉบับ ใช้กับการเปิดบัญชีต่างประเทศและการยืนยันตัวตนของกรรมการบริษัท",
    useCaseEn:
      "Overseas banks, brokers, crypto platforms and KYC providers routinely require a notary-certified copy of a passport instead of the original — common for offshore account opening and director verification.",
    prepareTh: [
      "ต้นฉบับตัวจริงทุกฉบับที่ต้องการรับรองสำเนา",
      "ผู้ถือเอกสารมาแสดงตนด้วย (กรณีหนังสือเดินทาง/บัตรประชาชน)",
      "ระบุจำนวนชุดที่ต้องการ เพราะแต่ละชุดต้องประทับตราแยก",
    ],
    prepareEn: [
      "The original of every document to be copied",
      "The document holder in person for passports and ID cards",
      "The number of sets required — each set is stamped separately",
    ],
    pitfallsTh: [
      "ส่งมาเฉพาะไฟล์สแกน — การรับรองสำเนาถูกต้องต้องเห็นต้นฉบับจริง",
      "ถ่ายสำเนาไม่ครบหน้า (เช่น หนังสือเดินทางที่ต้องการหน้าวีซ่าด้วย)",
      "สำเนาไม่ชัด ตราประทับหรือลายเซ็นในต้นฉบับอ่านไม่ออก",
    ],
    pitfallsEn: [
      "Sending scans only — a true-copy certification requires sighting the original",
      "Copying incomplete pages, e.g. omitting visa pages that the recipient needs",
      "Poor-quality copies where original stamps or signatures are illegible",
    ],
    nextStepTh:
      "หากปลายทางเป็นหน่วยราชการ ให้ต่อด้วยนิติกรณ์กรมการกงสุล ส่วนธนาคาร/KYC ส่วนใหญ่รับคำรับรองของทนายพร้อมข้อมูลใบอนุญาตโดยตรง",
    nextStepEn:
      "For government recipients continue with MFA legalisation; most banks and KYC providers accept the attorney's certification and licence details directly.",
    turnaroundTh: "รับรองได้ทันทีที่นัดหมาย โดยทั่วไปใช้เวลา 30–60 นาที",
    turnaroundEn: "Completed during the appointment, typically 30–60 minutes.",
  }),
  D({
    slug: "passport-copy",
    titleTh: "รับรองสำเนาหนังสือเดินทางสำหรับ KYC ธนาคารต่างประเทศ",
    titleEn: "Notarised Passport Copy for Overseas KYC",
    officialTh: "สำเนาหน้าข้อมูลหนังสือเดินทาง พร้อมหน้าวีซ่า/ตราประทับตรวจคนเข้าเมืองตามที่ปลายทางขอ",
    officialEn: "Copy of the passport biodata page plus visa or entry stamps where required",
    certTypeTh: "รับรองสำเนาถูกต้อง + รับรองตัวบุคคลว่าตรงกับรูปในหนังสือเดินทาง",
    certTypeEn: "True-copy certification together with identity verification against the passport photo",
    useCaseTh:
      "ใช้เปิดบัญชีธนาคาร/โบรกเกอร์ต่างประเทศ สมัคร Wise, Interactive Brokers, บัญชีสิงคโปร์-ฮ่องกง และยืนยันตัวตนผู้ถือหุ้น/กรรมการในบริษัทต่างประเทศ",
    useCaseEn:
      "Required to open overseas bank or brokerage accounts (Wise, Interactive Brokers, Singapore and Hong Kong banks) and to verify shareholders or directors of foreign companies.",
    prepareTh: [
      "หนังสือเดินทางตัวจริงที่ยังไม่หมดอายุ",
      "แบบฟอร์ม certification ของธนาคาร (ถ้ามี) — บางแห่งกำหนดถ้อยคำเฉพาะ",
      "หลักฐานที่อยู่ เช่น บิลค่าน้ำค่าไฟหรือทะเบียนบ้าน หากธนาคารขอ proof of address ด้วย",
    ],
    prepareEn: [
      "A valid, unexpired original passport",
      "The bank's own certification form if it prescribes specific wording",
      "Proof of address (utility bill or house registration) when the bank also requires it",
    ],
    pitfallsTh: [
      "ธนาคารกำหนดว่าใบรับรองต้องออกไม่เกิน 3 เดือน แต่ลูกค้าใช้ฉบับเก่า",
      "ไม่ใส่ข้อมูลผู้รับรอง (ชื่อ เลขใบอนุญาต ที่อยู่สำนักงาน) ตามที่ KYC ต้องการ",
      "รับรองเฉพาะหน้าแรก ทั้งที่ธนาคารขอหน้าลายเซ็นและหน้าตราประทับเข้าเมืองด้วย",
    ],
    pitfallsEn: [
      "Using an old certificate when the bank requires one issued within the last three months",
      "Omitting the certifier's name, licence number and office address that KYC teams require",
      "Certifying only the biodata page when the bank also wants the signature and entry-stamp pages",
    ],
    nextStepTh: "ส่วนใหญ่ส่งไฟล์สแกนสีให้ธนาคารได้ทันที และส่งตัวจริงตามไปรษณีย์ระหว่างประเทศเมื่อธนาคารร้องขอ",
    nextStepEn: "A colour scan is usually accepted immediately, with the hard copy couriered if the bank asks for it.",
    turnaroundTh: "เสร็จภายในวันเดียว · ส่งไฟล์สแกนให้ก่อนได้",
    turnaroundEn: "Same day, with a scanned copy sent ahead of the originals.",
  }),
  D({
    slug: "consent-to-travel",
    titleTh: "รับรองหนังสือยินยอมให้บุตรเดินทางต่างประเทศ",
    titleEn: "Notarised Parental Consent for a Child to Travel",
    officialTh: "หนังสือยินยอมของบิดา/มารดา (Parental Consent Letter) และเอกสารอำนาจปกครอง",
    officialEn: "Parental consent letter together with proof of custody",
    certTypeTh: "รับรองลายมือชื่อผู้ปกครองและการสาบานตน",
    certTypeEn: "Witnessing the parent's signature and administering the declaration",
    useCaseTh:
      "ใช้เมื่อบุตรเดินทางกับผู้ปกครองเพียงคนเดียว กับญาติ หรือเดินทางลำพัง สายการบินและด่านตรวจคนเข้าเมืองหลายประเทศขอหนังสือยินยอมที่มีการรับรองลายมือชื่อ และมักใช้ประกอบการยื่นวีซ่านักเรียนของผู้เยาว์",
    useCaseEn:
      "Needed when a child travels with one parent, with relatives or unaccompanied. Airlines and immigration authorities frequently require a witnessed consent letter, which is also used to support minors' student visa applications.",
    prepareTh: [
      "สูติบัตรของบุตร และทะเบียนบ้านที่แสดงความสัมพันธ์",
      "หนังสือเดินทาง/บัตรประชาชนของบิดาและมารดาทั้งสองฝ่าย",
      "หากหย่าแล้ว ให้เตรียมบันทึกข้อตกลงการหย่าหรือคำสั่งศาลที่ระบุอำนาจปกครอง",
      "รายละเอียดการเดินทาง: ประเทศปลายทาง ช่วงวันที่ ผู้ดูแลระหว่างเดินทาง",
    ],
    prepareEn: [
      "The child's birth certificate and house registration showing the relationship",
      "Passports or ID cards of both parents",
      "If divorced, the divorce agreement or court order confirming custody",
      "Travel details: destination, dates and the accompanying guardian",
    ],
    pitfallsTh: [
      "มีผู้ปกครองมาลงนามเพียงคนเดียวโดยไม่มีหลักฐานอำนาจปกครองแต่ผู้เดียว",
      "ไม่ระบุช่วงวันเดินทางและประเทศปลายทาง ทำให้สายการบินไม่รับ",
      "ชื่อบุตรในหนังสือยินยอมสะกดต่างจากหนังสือเดินทาง",
    ],
    pitfallsEn: [
      "Only one parent signs without evidence of sole custody",
      "Omitting travel dates and destination, which airlines reject",
      "The child's name spelled differently from the passport",
    ],
    nextStepTh: "หากปลายทางเป็นหน่วยงานราชการหรือสถานทูต ให้ต่อด้วยนิติกรณ์กรมการกงสุลและรับรองที่สถานทูตปลายทาง",
    nextStepEn: "For embassy or government use, follow with MFA legalisation and destination embassy attestation.",
    turnaroundTh: "เสร็จภายในวันเดียว ควรนัดล่วงหน้าเมื่อผู้ปกครองต้องมาพร้อมกันสองคน",
    turnaroundEn: "Same day; book ahead when both parents must attend together.",
  }),
  D({
    slug: "academic-documents",
    titleTh: "รับรองปริญญาบัตรและทรานสคริปต์",
    titleEn: "Notarisation of Degree Certificates and Transcripts",
    officialTh: "ปริญญาบัตร ใบรับรองผลการศึกษา (Transcript) และหนังสือรับรองสถานภาพนักศึกษา",
    officialEn: "Degree certificate, academic transcript and student status letter",
    certTypeTh: "รับรองสำเนาถูกต้องของเอกสารการศึกษา และ/หรือ รับรองลายมือชื่อผู้แปล",
    certTypeEn: "True-copy certification of academic records and certification of the translator's signature",
    useCaseTh:
      "ใช้สมัครเรียนต่อและขอวีซ่านักเรียนต่างประเทศ สมัครงานกับนายจ้างต่างชาติ ยื่นประเมินวุฒิ (credential evaluation) เช่น WES และยื่นขอใบอนุญาตทำงานในต่างประเทศ",
    useCaseEn:
      "Used for overseas study applications and student visas, foreign employers, credential evaluations such as WES, and overseas work permit applications.",
    prepareTh: [
      "ปริญญาบัตรและทรานสคริปต์ตัวจริงที่ออกโดยสถาบัน",
      "คำแปลภาษาอังกฤษ (หากสถาบันไม่ได้ออกฉบับภาษาอังกฤษให้)",
      "หนังสือเดินทางของเจ้าของเอกสาร",
    ],
    prepareEn: [
      "Original degree certificate and transcript issued by the institution",
      "An English translation where the institution does not issue an English version",
      "The holder's passport",
    ],
    pitfallsTh: [
      "สถาบันปลายทางต้องการทรานสคริปต์ที่ส่งตรงจากมหาวิทยาลัยในซองปิดผนึก ซึ่งการรับรองสำเนาแทนไม่ได้",
      "แปลชื่อวิชา/เกรดไม่ตรงกับระบบของสถาบัน",
      "ปริญญาบัตรฉบับภาษาไทยไม่ได้แปลครบทุกตราประทับ",
    ],
    pitfallsEn: [
      "The receiving institution wants a sealed transcript sent directly by the university, which a certified copy cannot replace",
      "Course titles or grading scales translated inconsistently with the institution's system",
      "Thai-language certificates translated without every seal and stamp",
    ],
    nextStepTh:
      "เอกสารการศึกษาที่ออกโดยสถาบันของรัฐสามารถนำไปนิติกรณ์กรมการกงสุลได้ หลายประเทศต้องการขั้นนี้ก่อนรับรองที่สถานทูต",
    nextStepEn:
      "Academic documents from state institutions can be legalised at the MFA; many countries require this step before embassy attestation.",
    turnaroundTh: "รับรอง 1 วันทำการ · รวมงานแปลรับรอง 2–3 วันทำการ",
    turnaroundEn: "One working day for notarisation; 2–3 working days including certified translation.",
  }),
  D({
    slug: "company-documents",
    titleTh: "รับรองเอกสารบริษัทและหนังสือรับรองนิติบุคคล",
    titleEn: "Notarisation of Corporate Documents",
    officialTh: "หนังสือรับรองบริษัท (DBD) บัญชีรายชื่อผู้ถือหุ้น (บอจ.5) หนังสือบริคณห์สนธิ และรายงานการประชุม",
    officialEn: "DBD company affidavit, shareholder list (Bor.Or.Jor.5), memorandum of association and board minutes",
    certTypeTh: "รับรองสำเนาถูกต้อง และรับรองลายมือชื่อกรรมการผู้มีอำนาจ",
    certTypeEn: "True-copy certification and witnessing of the authorised director's signature",
    useCaseTh:
      "ใช้เปิดบัญชีธนาคารต่างประเทศในนามบริษัท จดทะเบียนบริษัทลูก/สาขาในต่างประเทศ ทำสัญญาข้ามพรมแดน ยื่นประมูลงานต่างประเทศ และจดทะเบียนเครื่องหมายการค้าระหว่างประเทศ",
    useCaseEn:
      "Used to open corporate bank accounts abroad, register subsidiaries or branches overseas, execute cross-border contracts, bid on foreign tenders and file international trademark applications.",
    prepareTh: [
      "หนังสือรับรองบริษัทจากกรมพัฒนาธุรกิจการค้าที่ออกไม่เกิน 3–6 เดือน",
      "บัตรประชาชน/หนังสือเดินทางของกรรมการผู้มีอำนาจลงนาม",
      "ตราประทับบริษัท (ถ้าหนังสือรับรองระบุว่าต้องประทับ)",
      "คำแปลภาษาอังกฤษของหนังสือรับรองและ บอจ.5",
    ],
    prepareEn: [
      "A DBD company affidavit issued within the last 3–6 months",
      "ID or passport of the authorised signing director",
      "The company seal where the affidavit requires it",
      "English translations of the affidavit and shareholder list",
    ],
    pitfallsTh: [
      "กรรมการลงนามไม่ครบตามเงื่อนไขอำนาจลงนามในหนังสือรับรอง",
      "ใช้หนังสือรับรองเก่าเกินกำหนดของธนาคารปลายทาง",
      "ไม่ได้แปลรายชื่อผู้ถือหุ้น ทำให้ธนาคารตรวจ UBO ไม่ได้",
    ],
    pitfallsEn: [
      "Not all directors sign as required by the signing authority in the affidavit",
      "Relying on an affidavit older than the recipient bank allows",
      "Leaving the shareholder list untranslated so the bank cannot verify the UBO",
    ],
    nextStepTh:
      "เอกสารที่ออกโดย DBD นำไปนิติกรณ์กรมการกงสุลได้โดยตรง จากนั้นจึงรับรองที่สถานทูตของประเทศที่จะใช้เอกสาร",
    nextStepEn:
      "DBD-issued documents can be legalised directly at the MFA and then attested at the destination country's embassy.",
    turnaroundTh: "รับรอง 1 วันทำการ · ครบชุดพร้อมนิติกรณ์ 3–5 วันทำการ",
    turnaroundEn: "One working day to notarise; 3–5 working days for the full legalised set.",
  }),
  D({
    slug: "contracts-agreements",
    titleTh: "รับรองสัญญาและข้อตกลงระหว่างประเทศ",
    titleEn: "Notarisation of Contracts and Cross-Border Agreements",
    officialTh: "สัญญาซื้อขาย สัญญาจ้าง สัญญาร่วมทุน สัญญาเช่า และบันทึกข้อตกลง (MOU)",
    officialEn: "Sale, employment, joint venture and lease agreements, and memoranda of understanding",
    certTypeTh: "รับรองลายมือชื่อคู่สัญญาและวันเวลาที่ลงนาม (Signature & Date Witnessing)",
    certTypeEn: "Witnessing the parties' signatures and the date of execution",
    useCaseTh:
      "ใช้ยืนยันว่าคู่สัญญาลงนามจริงต่อหน้าทนาย ลดข้อโต้แย้งเรื่องความแท้ของลายมือชื่อเมื่อเกิดข้อพิพาทข้ามประเทศ และมักถูกกำหนดในสัญญาที่มีคู่สัญญาต่างชาติหรือทรัพย์สินในต่างประเทศ",
    useCaseEn:
      "Confirms the parties signed in the attorney's presence, reducing disputes over signature authenticity in cross-border matters — often mandated where a counterparty or asset sits abroad.",
    prepareTh: [
      "สัญญาฉบับสมบูรณ์ที่ยังไม่ได้ลงนาม",
      "บัตรประชาชน/หนังสือเดินทางของผู้ลงนามทุกฝ่าย",
      "หนังสือรับรองบริษัทและหลักฐานอำนาจลงนาม กรณีคู่สัญญาเป็นนิติบุคคล",
    ],
    prepareEn: [
      "The final, unsigned contract",
      "ID or passport of every signatory",
      "Company affidavit and evidence of signing authority for corporate parties",
    ],
    pitfallsTh: [
      "ลงนามล่วงหน้าแล้วนำมาให้รับรองภายหลัง",
      "ไม่ระบุกฎหมายที่ใช้บังคับและเขตอำนาจศาล",
      "คิดว่าการรับรองของทนายเท่ากับการรับรองเนื้อหาสัญญา — ทนายรับรองเฉพาะลายมือชื่อ",
    ],
    pitfallsEn: [
      "Signing in advance and presenting the contract for certification afterwards",
      "Omitting governing law and jurisdiction clauses",
      "Assuming notarisation certifies the contract terms — it certifies the signatures only",
    ],
    nextStepTh: "หากต้องยื่นต่อหน่วยงานต่างประเทศ ให้ต่อด้วยนิติกรณ์กรมการกงสุลและสถานทูตปลายทาง",
    nextStepEn: "For filing with a foreign authority, continue with MFA legalisation and embassy attestation.",
    turnaroundTh: "นัดลงนามและรับรองเสร็จในครั้งเดียว โดยทั่วไป 1 ชั่วโมง",
    turnaroundEn: "Signing and notarisation completed in a single appointment, typically within an hour.",
  }),
  D({
    slug: "bank-forms",
    titleTh: "รับรองแบบฟอร์มธนาคารและตัวอย่างลายมือชื่อ",
    titleEn: "Notarisation of Bank Forms and Specimen Signatures",
    officialTh: "แบบฟอร์มเปิดบัญชี แบบฟอร์ม W-8BEN แบบยืนยันลายมือชื่อ และหนังสือยินยอมโอนเงิน",
    officialEn: "Account opening forms, W-8BEN forms, specimen signature forms and remittance authorisations",
    certTypeTh: "รับรองลายมือชื่อและตัวอย่างลายมือชื่อ (Specimen Signature Verification)",
    certTypeEn: "Signature witnessing and specimen signature verification",
    useCaseTh:
      "ธนาคารและสถาบันการเงินต่างประเทศมักกำหนดให้ลูกค้าที่อยู่นอกประเทศลงนามต่อหน้า notary เพื่อยืนยันตัวตน ใช้กับการเปิดบัญชี การเรียกร้องเงินประกัน กองทุน และการรับมรดกจากบัญชีต่างประเทศ",
    useCaseEn:
      "Overseas banks and financial institutions commonly require non-resident clients to sign before a notary — for account opening, insurance and fund claims, and inheriting overseas accounts.",
    prepareTh: [
      "แบบฟอร์มของสถาบันการเงินฉบับล่าสุด (ยังไม่ลงนาม)",
      "หนังสือเดินทางตัวจริง",
      "จดหมายหรืออีเมลจากธนาคารที่ระบุข้อกำหนดการรับรอง",
    ],
    prepareEn: [
      "The institution's current, unsigned form",
      "Original passport",
      "The bank's letter or email setting out its certification requirements",
    ],
    pitfallsTh: [
      "ธนาคารกำหนดถ้อยคำ certification เฉพาะแต่ใช้ตราประทับมาตรฐานแทน",
      "ลงวันที่ในแบบฟอร์มไม่ตรงกับวันที่รับรอง",
      "ธนาคารบางแห่งต้องการ Apostille ซึ่งไทยยังไม่มี ต้องใช้เส้นทางนิติกรณ์ MFA + สถานทูตแทน",
    ],
    pitfallsEn: [
      "Using a standard stamp when the bank prescribes specific certification wording",
      "A form date that differs from the notarisation date",
      "Banks asking for an apostille, which Thailand does not issue — the MFA plus embassy route is used instead",
    ],
    nextStepTh: "ส่งสแกนสีให้ธนาคารตรวจก่อน แล้วจึงส่งตัวจริงทางไปรษณีย์ระหว่างประเทศ",
    nextStepEn: "Send a colour scan for the bank's pre-check, then courier the originals.",
    turnaroundTh: "เสร็จภายในวันเดียว",
    turnaroundEn: "Same day.",
  }),
  D({
    slug: "property-deed-overseas",
    titleTh: "รับรองเอกสารซื้อขายอสังหาริมทรัพย์ในต่างประเทศ",
    titleEn: "Notarisation of Overseas Property Transfer Documents",
    officialTh: "Deed, Quitclaim Deed, Closing Documents และหนังสือมอบอำนาจขายทรัพย์",
    officialEn: "Deeds, quitclaim deeds, closing packages and property-specific powers of attorney",
    certTypeTh: "รับรองลายมือชื่อและการรับรู้ข้อความ (Acknowledgement) ตามแบบที่รัฐปลายทางกำหนด",
    certTypeEn: "Signature witnessing and acknowledgement in the form prescribed by the destination jurisdiction",
    useCaseTh:
      "คนไทยหรือชาวต่างชาติที่อยู่ในไทยและต้องขาย/โอนอสังหาริมทรัพย์ในสหรัฐฯ ออสเตรเลีย หรือยุโรป มักต้องลงนามต่อหน้า notary และในบางรัฐของสหรัฐฯ ต้องมีพยานเพิ่มอีก 1–2 คน",
    useCaseEn:
      "Thais and foreigners in Thailand selling or transferring property in the US, Australia or Europe usually must sign before a notary, and some US states additionally require one or two witnesses.",
    prepareTh: [
      "ชุดเอกสารปิดการขายจาก title company หรือทนายปลายทาง",
      "หนังสือเดินทางตัวจริงของผู้ลงนามทุกคน",
      "คำแนะนำการรับรอง (notarial wording) ที่รัฐปลายทางกำหนด",
      "พยานตามจำนวนที่กำหนด หากรัฐปลายทางบังคับ",
    ],
    prepareEn: [
      "The closing package from the title company or destination lawyer",
      "Original passports of all signatories",
      "The notarial wording prescribed by the destination jurisdiction",
      "The required number of witnesses where mandated",
    ],
    pitfallsTh: [
      "ใช้ถ้อยคำรับรองผิดแบบของรัฐ ทำให้ title company ปฏิเสธและต้องเซ็นใหม่",
      "ไม่ทราบว่าต้องมีพยานเพิ่มเติมนอกจาก notary",
      "ตกหล่นขั้นตอนนิติกรณ์กรมการกงสุลและสถานทูตสหรัฐฯ/ปลายทาง",
    ],
    pitfallsEn: [
      "Using the wrong state-specific notarial wording, forcing a re-signing at the title company's request",
      "Not realising that separate witnesses are required in addition to the notary",
      "Missing the MFA and destination embassy legalisation steps",
    ],
    nextStepTh:
      "หลังรับรอง ให้ตรวจกับ title company หรือทนายปลายทางว่าต้องนิติกรณ์กรมการกงสุลและรับรองที่สถานทูตปลายทางหรือไม่ ก่อนส่งเอกสารด้วยไปรษณีย์ด่วนระหว่างประเทศ",
    nextStepEn:
      "Confirm with the title company or destination lawyer whether MFA and embassy legalisation are needed before couriering the documents.",
    turnaroundTh: "นัดลงนาม 1 วัน · จัดส่งต่างประเทศ 3–5 วันทำการ",
    turnaroundEn: "One day for the signing appointment; 3–5 working days for international courier.",
  }),
  D({
    slug: "inheritance-estate",
    titleTh: "รับรองเอกสารมรดกและทายาทในต่างประเทศ",
    titleEn: "Notarisation of Inheritance and Estate Documents",
    officialTh: "หนังสือสละมรดก หนังสือรับรองทายาท คำร้องขอรับมรดกจากบัญชี/ประกันในต่างประเทศ",
    officialEn: "Deeds of renunciation, heirship declarations and claims on overseas accounts or policies",
    certTypeTh: "รับรองลายมือชื่อทายาทและการสาบานตนในคำให้การเกี่ยวกับทายาท",
    certTypeEn: "Witnessing heirs' signatures and administering declarations of heirship",
    useCaseTh:
      "ใช้เมื่อผู้เสียชีวิตมีทรัพย์สิน บัญชีธนาคาร หรือกรมธรรม์ในต่างประเทศ และทายาทซึ่งอยู่ในไทยต้องยื่นเอกสารต่อศาลหรือสถาบันการเงินปลายทาง",
    useCaseEn:
      "Used when a deceased person held assets, bank accounts or policies abroad and heirs living in Thailand must file with a foreign court or financial institution.",
    prepareTh: [
      "ใบมรณบัตรและคำแปลภาษาอังกฤษ",
      "เอกสารแสดงความสัมพันธ์ เช่น ทะเบียนบ้าน สูติบัตร ทะเบียนสมรส",
      "คำสั่งศาลตั้งผู้จัดการมรดก (ถ้ามี)",
      "แบบฟอร์มของสถาบันการเงินหรือศาลปลายทาง",
    ],
    prepareEn: [
      "Death certificate with English translation",
      "Proof of relationship: house registration, birth certificate or marriage certificate",
      "The court order appointing the estate administrator, if any",
      "The destination institution's or court's own forms",
    ],
    pitfallsTh: [
      "ทายาทไม่ครบตามที่ปลายทางกำหนดให้ลงนามร่วม",
      "แปลใบมรณบัตรโดยไม่ได้รับรองคำแปลตามที่ปลายทางต้องการ",
      "ประเมินเวลาน้อยเกินไป เพราะขั้นตอนนิติกรณ์และสถานทูตใช้เวลาหลายวัน",
    ],
    pitfallsEn: [
      "Not all heirs sign as the destination requires",
      "Translating the death certificate without the certification the recipient requires",
      "Underestimating timelines, as legalisation and embassy steps take several days",
    ],
    nextStepTh: "เอกสารราชการไทย เช่น ใบมรณบัตร ต้องผ่านนิติกรณ์กรมการกงสุลก่อน แล้วจึงรับรองที่สถานทูตปลายทาง",
    nextStepEn: "Thai civil documents such as death certificates must be MFA-legalised before embassy attestation.",
    turnaroundTh: "รับรอง 1 วันทำการ · ครบชุดพร้อมนิติกรณ์และสถานทูต 5–10 วันทำการ",
    turnaroundEn: "One working day to notarise; 5–10 working days for the complete legalised set.",
  }),
  D({
    slug: "trademark-ip",
    titleTh: "รับรองหนังสือมอบอำนาจเครื่องหมายการค้าและทรัพย์สินทางปัญญา",
    titleEn: "Notarisation of Trademark and IP Powers of Attorney",
    officialTh: "Power of Attorney สำหรับตัวแทนเครื่องหมายการค้า/สิทธิบัตร และ Declaration of Ownership",
    officialEn: "Trademark or patent agent powers of attorney and declarations of ownership",
    certTypeTh: "รับรองลายมือชื่อผู้มีอำนาจลงนามของเจ้าของสิทธิ",
    certTypeEn: "Witnessing the signature of the rights holder's authorised signatory",
    useCaseTh:
      "ใช้ยื่นจดทะเบียนเครื่องหมายการค้าในต่างประเทศ ระบบ Madrid Protocol ผ่าน WIPO และการยื่นสิทธิบัตรผ่านตัวแทนในต่างประเทศ ซึ่งหลายประเทศกำหนดให้ POA ต้องผ่านการรับรองลายมือชื่อ",
    useCaseEn:
      "Required for overseas trademark filings, Madrid Protocol applications via WIPO and patent filings through foreign agents, many of which require a witnessed power of attorney.",
    prepareTh: [
      "แบบฟอร์ม POA ของสำนักงานตัวแทนปลายทาง",
      "หนังสือรับรองบริษัทและหลักฐานอำนาจลงนาม",
      "รายละเอียดเครื่องหมาย/สิทธิบัตร: เลขคำขอ ประเทศที่ยื่น กลุ่มสินค้า",
    ],
    prepareEn: [
      "The destination agent's POA form",
      "Company affidavit and evidence of signing authority",
      "Mark or patent details: application number, filing countries and classes",
    ],
    pitfallsTh: [
      "ชื่อเจ้าของสิทธิใน POA ไม่ตรงกับชื่อในทะเบียนนิติบุคคล",
      "ระบุกลุ่มสินค้า/บริการไม่ครบตามที่ยื่นจริง",
      "ไม่เผื่อเวลานิติกรณ์ ทำให้พลาดกำหนดวันยื่นตามสิทธิความสำคัญ (priority date)",
    ],
    pitfallsEn: [
      "The rights holder's name on the POA not matching the corporate register",
      "Listing incomplete classes of goods or services",
      "Not allowing time for legalisation and missing the priority date",
    ],
    nextStepTh: "ตรวจกับตัวแทนปลายทางว่าต้องนิติกรณ์กรมการกงสุลและสถานทูตหรือไม่ บางประเทศรับ POA ที่ผ่าน notary อย่างเดียว",
    nextStepEn: "Check with the destination agent whether MFA and embassy legalisation are needed; some countries accept notarisation alone.",
    turnaroundTh: "เสร็จภายในวันเดียว · เร่งด่วนได้เมื่อใกล้ priority date",
    turnaroundEn: "Same day, with expedited handling near a priority deadline.",
  }),
];

export function getNotaryDocLanding(slug: string): NotaryDocLanding | undefined {
  return NOTARY_DOC_LANDINGS.find((d) => d.slug === slug);
}